/* eslint-disable */
import { ProductPermissions } from '../constants/constants';

export interface Error {
  code: string;
  message: string;
}

export interface APIError {
  traceId: string;
  requestId: string;
  errors: Error[];
}

export interface User {
  readonly id: string;
  readonly employeeId?: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly route: string;
  readonly permissions: ProductPermissions[];
}

export interface Action<T1 = any, T2 = any> {
  type: T1;
  payload?: T2;
}

export interface Option {
  label: string;
  value: string;
}

export interface ReferenceResponse {
  referenceKey: ReferenceKeys;
  referenceDetails: ReferenceItem[];
}

export interface ReferenceItem {
  readonly referenceCode: string;
  readonly displaySeq: number;
  readonly defaultValue: string;
  readonly description: string;
  readonly descSpanish?: string;
}

export interface ScheduleFieldVisitDTO {
  name: string;
  customerId: string;
  agreementNumber: string;
}

export enum ReferenceKeys {
  BEST_TIME_TO_CALL = 'CALL_TIME',
  COMMITMENT_STATUS = 'COMMITMENT_STATUS',
  LANGUAGE = 'LANGUAGE',
  ROUTE = 'ROUTE',
  CALL_RESULT = 'CALL_RESULT',
  FIELD_VISIT_RESULT = 'FIELD_VISIT_RESULT',
  COMMITMENT_TYPE = 'COMMITMENT_TYPE',

  COWORKER = 'COWORKER',

  AM_ACTIVITY = 'CALL_RESULT',
  PHONE_TYPE = 'PHONE_TYPE',
  DAYS_LATE = 'DAYS_LATE',
}

export const EVENT_TYPES: Record<string, string> = {
  SE: 'Store Event',
  D: 'Delivery',
  CO: 'Carry Out',
  CI: 'CarryIn',
  S: 'Service',
  SCI: 'Service Carry In',
  SCO: 'Service Carry Out',
  STOPIC: 'Store Pick-up',
  FIELD: 'Field Visit',
  P: 'Pickup',
  G: 'General',
  SO: 'Switchout',
  SP: 'Service Pickup',
  TR: 'Transfer',
  TRD: 'Transfer Delivery',
  TRP: 'Transfer Pickup',
  AD: 'Acima Delivery',
};
export function getEventType(refCode: string): string | undefined {
  return EVENT_TYPES[refCode];
}

export interface ActivityPayload {
  storeNumber: string;
  activityDate: string;
  notes: string;
  callResultRefCode: string;
  fieldVisitResultRefCode?: string;
  commitmentId?: number;
  acctActivityRefCode: string;
  daysPastDue?: string;
  customerId: number;
  phoneNumberDialed?: string;
  coWorkerId: string;
  receiptId?: number;
  callTime: string;
}

export interface CustomerLocationState {
  customerIndex: number;
  commitmentDetails: any;
}

export interface FieldSheetResponse {
  customersFieldSheet: {
    fieldSheets: [   
      {
        customer: FieldSheetResponseCustomer[];
      }
    ];
    storeAddress: {
      addressLine1: string;
      city: string;
      postalCode: string;
      state: string;   
    };
    storeName: string;
    storeNumber: string;
  };
}

export interface FieldSheetResponseCustomer {
  corenter: {
    globalCustomerId: string;
    customerId: string;
    firstName: string;
    middleInitial: string;
    lastName: string;
    suffix: string;
    emailAddress: string;
    customerSinceDate: string;
    phones: [
      {
        callTimeType: { code: string; description: string };
        extension: string;
        note: string;
        phoneNumber: string;
        phoneType: { code: string; description: string };
        primary: string;
      }
    ];
  };      
  addresses: [
    {
      addressLine1: string;
      addressLine2: string;
      city: string;
      postalCode: string;
      state: string;
      addressTypeCode: string;
      addressTypeDesc: string;
    }
  ];
  alerts: [
    {
      alertCleared: string;
      alertText: string;
      alertTime: string;
      alertType: string;
    }
  ];
  commitmentInfo: {
    commitmentsSinceDueDate: [
      {
        commitmentDate: string;
        amount: string;
        code: string;
        description: string;
        memo: string;
      }
    ];
    totals: {
      broken: string;
      brokenPaid: string;
      honored: string;
      open: string;
      revised: string;
      total: string;
    };
  };
  currentAgreementInfo: {
    agreements: Agreement[];
  };
  currentClubInfo: {
    activeSince: string;
    clubName: string;
    dueDate: string;
    pastDue: string;
    payments: [
      {
        amountPaid: string;
        dueDate: string;
        paidOn: string;
        paymentMethod: string;
        receiptNumber: string;

        rental: string;
        tax: string;
        freeDays: string;
        daysLate: string;
        tendered: string;
        payoffDate: string;
      }
    ];
  };
  customerCreditInfo: {
    coa: string;
    promo: string;
    promoFreeDays: string;
    sips: string;
    suspense: string;
    total: string;
  };
  customerId: string;
  customerSinceDate: string;
  emailAddress: string;
  employerReferences: [
    {
      active: string;
      employerName: string;
      employerPhoneExtension: string;
      employerPhoneNumber: string;
      employmentBeginDate: string;
      jobTitle: string;
      supervisorFirstName: string;
      supervisorLastName: string;
      workEndTime: string;
      workStartTime: string;
      addressLine1: string | null;
      addressLine2: string | null;
      city: string | null;
      state: string | null;
    }
  ];
  firstName: string;
  globalCustomerId: string;
  grandTotalsAllPastDue: {
    carriedLate: string;
    carriedRent: string;
    club: string;
    deferredRent: string;
    lateFees: string;
    ldw: string;
    otherFees: string;
    rent: string;
    tax: string;
    total: string;
  };
  lastName: string;
  middleInitial: string;
  nsfAndChargebackInfo: {
    achTotalAmount: string;
    achTotalNumber: string;
    chargeBackTotalAmount: string;
    chargeBackTotalNumber: string;
    nsfTotalAmount: string;
    nsfTotalNumber: string;
  };
  pastDueAgreementInfo: [
    {
      agreementNumber: string;
      inventory: [
        {
          brand: string;
          department: string;
          itemNumber: string;
          modelNumber: string;
          serialNumber: string;
          subDepartment: string;
        }
      ];
      nextDueDate: string;
      openDate: string;
      pastDue: string;
      pastDue1to6: string;
      pastDue7to14: string;
      pastDue15Plus: string;
      payments: [
        {
          daysLate: string;
          dueDate: string;
          freeDays: string;
          paidOn: string;
          paymentMethod: string;
          paymentOrigin: string;
          payoffDate: string;
          receiptNumber: string;
          rental: string;
          tax: string;
        }
      ];
      totals: {
        carriedLate: string;
        carriedRent: string;
        deferredRent: string;
        lateFees: string;
        ldw: string;
        otherFees: string;
        rent: string;
        tax: string;
        total: string;
      };
    }
  ];

  pastDueClubInfo: {
    activeSince: string;
    clubName: string;
    dueDate: string;
    pastDue: string;
    payments: [
      {
        amountPaid: string;
        dueDate: string;
        paidOn: string;
        paymentMethod: string;
        receiptNumber: string;
      }
    ];
  };
  phones: [
    {
      callTimeType: { code: string; description: string };
      extension: string;
      note: string;
      phoneNumber: string;
      phoneType: { code: string; description: string };
      primary: string;
    }
  ];
  productBalances: [
    {
      agreementNumber: string;
      epo: string;
      paidIn: string;
      remainingValue: string;
    }
  ];
  route: {
    code: string;
    description: string;
  };
  suffix: string;
  vehicles: [
    {
      vehFinancedFrom: string;
      vehMonthlyPayment: string;
      vehPlanExpirationDate: string;
      vehRegState: string;
      vehicleColor: string;
      vehicleIndex: string;
      vehicleLicensePlate: string;
      vehicleMake: string;
      vehicleModel: string;
      vehicleVin: string;
      vehicleYear: string;
    }
  ];
  workedHistoryInfo: {
    totalWorkedHistoryDays: string;
    workedHistoryDays: string;
    workedItems: [
      {
        callResult: string;
        coWorker: string;
        contact: string;
        phoneNumber: string;
        relation: string;
        workedDate: string;
      }
    ];
  };
}

export interface Agreement {
  agreementNumber: string;
  inventory: [
    {
      brand: string;
      department: string;
      itemNumber: string;
      modelNumber: string;
      serialNumber: string;
      subDepartment: string;
    }
  ];
  nextDueDate: string;
  openDate: string;
  pastDue: string;
  pastDue1to6: string;
  pastDue7to14: string;
  pastDue15Plus: string;
  payments: any[];
  totals: {
    carriedLate: string;
    carriedRent: string;
    deferredRent: string;
    lateFees: string;
    ldw: string;
    otherFees: string;
    rent: string;
    tax: string;
    total: string;
  };
}

export interface GetCustomerFieldSheetsPayload {
  storeNumber: string;
  workedHistoryType: string;
  customerIds: string[];
}
export interface StoreConfigsPayload {
  storeNumbers: string[];
  paramKeyNames: string[];
}

export interface StoreConfigsResponse {
  storeNumber: string;
  configDetails: StoreConfigDetails[];
}

export interface StoreConfigDetails {
  paramValue: string;
  paramKeyName: string;
  paramGroupName: string;
  paramCategoryName: string;
  paramKeyId: string;
  paramHierarchy: string;
}

export interface createFieldVisitEventCustomer {
  source: string;
  customerInfo: {
    customerId: string;
    globalCustomerId: string;
    firstName: string;
    lastName: string;
    address: {
      addressLine1: string;
      addressLine2: string;
      city: string;
      postalCode: string;
      state: string;
    };
  };
  inventories: [
    {
      inventoryNumber: string;
    }
  ];
  agreement: {
    agreementNumber: string;
  };
}

export interface FieldSheet {
  section1: FieldSheetSection1;
  section2: FieldSheetSection2;
  section2Corenter: FieldSheetSection2;
  section3NSFChargeback: FieldSheetSection3NSFChargeback;
  section3CustomerCredits: FieldSheetSection3CustomerCredits;
  section4CustomerAlerts: FieldSheetSection4CustomerAlerts[];
  section5ProductBalances: FieldSheetSection5ProductBalances[];
  section6: FieldSheetSection6[];
  section10Club: FieldSheetSection10Club;
  section11PaymentsLast3: FieldSheetSection11PaymentsLast3[];
  section12GrandTotal: FieldSheetSection12GrandTotalAllPastDue;
  section14Total: FieldSheetSection14Total;
  section14WorkedHistory: FieldSheetSection14WorkedHistory[];
  section15TotalCommitments: FieldSheetSection15TotalCommitments;
  section16CommitmentsSinceDueDate: FieldSheetSection16CommitmentsSinceDueDate[];
}

export interface FieldSheetSection1 {
  route: string;
  store: string;
  delivery: string;
  date: string;
}

export interface FieldSheetSection2 {
  customerColumn: string;
  workColumn: string;
  vehicleColumn: string;
  phoneColumn: string;
}

export interface FieldSheetSection3NSFChargeback {
  nsfTotalNumber: string;
  nsfTotalAmount: string;
  achTotalNumber: string;
  achTotalAmount: string;
  chargebackTotalNumber: string;
  chargebackTotalAmount: string;
}

export interface FieldSheetSection3CustomerCredits {
  promoFreeDays: string;
  suspense: string;
  promo: string;
  sips: string;
  coa: string;
  total: string;
}

export interface FieldSheetSection4CustomerAlerts {
  alertTime: string;
  alertText: string;
}

export interface FieldSheetSection5ProductBalances {
  agreementNumber: string;
  epo: string;
  remainingValue: string;
  paidIn: string;
}

export interface FieldSheetSection6 {
  section6PastDueAgreements: FieldSheetSection6PastDueAgreements;
  section6Inventory: FieldSheetSection7Inventory[];
  section6Payments: FieldSheetSection8Payments[];
  section6Total: FieldSheetSection9Total;
}

export interface FieldSheetSection6Retention {
  agreementNumber?: string;
  date?: string;
  offer?: string;
}
export interface FieldSheetSection6PastDueAgreements {
  agreementNumber: string;
  date: string;
  pastDue1to6: string;
  pastDue7to14: string;
  pastDue15Plus: string;
  nextDueDate: string;
  pastDue: string;
  agreementRetention: FieldSheetSection6Retention[];
}

export interface FieldSheetSection7Inventory {
  department: string;
  subDepartment: string;
  brand: string;
  modelNumber: string;
  serialNumber: string;
  itemNumber: string;
}

export interface FieldSheetSection8Payments {
  paidOn: string;
  receipt: string;
  rental: string;
  tax: string;
  dueDate: string;
  freeDays: string;
  daysLate: string;
  type: string;
  tendered: string;
  payOffDate: string;
}

export interface FieldSheetSection9Total {
  rent: string;
  ldw: string;
  carriedLate: string;
  deferredRent: string;
  carriedRent: string;
  otherFees: string;
  lateFees: string;
  tax: string;
  total: string;
}

export interface FieldSheetSection10Club {
  club: string;
  activeSince: string;
  dueDate: string;
  pastDue: string;
}

export interface FieldSheetSection11PaymentsLast3 {
  paidOn: string;
  receipt: string;
  amountPaid: string;
  dueDate: string;
  tendered: string;
}

export interface FieldSheetSection12GrandTotalAllPastDue {
  rent: string;
  ldw: string;
  carriedLate: string;
  deferredRent: string;
  carriedRent: string;
  otherFees: string;
  lateFees: string;
  tax: string;
  total: string;
  club: string;
}

export interface FieldSheetSection14Total {
  totalWorkedHistoryDays: string;
  workedHistoryDays: string;
}

export interface FieldSheetSection14WorkedHistory {
  date: string;
  phoneDialed: string;
  workedResult: string;
  employee: string;
  relation: string;
  contactName: string;
}

export interface FieldSheetSection15TotalCommitments {
  total: string;
  open: string;
  honored: string;
  broken: string;
  revised: string;
  brokenPaid: string;
}

export interface FieldSheetSection16CommitmentsSinceDueDate {
  date: string;
  time: string;
  status: string;
  amount: string;
  memo: string;
}

export interface FieldVisitEventPopupProps {
  customerName: string;
  customerId: string;
}

export interface StoreDetail {
  companyName: string;
  companyCode: string;
  storeType: string;
  storeId: string;
  storeNumber: string;
  storeName: string;
  lob: string;
  countryAbb: string;
  stateName: string;
  stateAbb: string;
  city: string;
  zip: string;
  fullZip: string;
  addressLine1: string;
  workPhoneNumber: string;
  latitude: string;
  longitude: string;
}
export interface CustomerSkipTraceRequestLevelsType {
  [key: string]: number[];
}

export interface StoreDetailResponse {
  response: StoreDetail[];
}

export interface createFieldVisitEventCustomer {
  source: string;
  customerInfo: {
    customerId: string;
    globalCustomerId: string;
    firstName: string;
    lastName: string;
    address: {
      addressLine1: string;
      addressLine2: string;
      city: string;
      postalCode: string;
      state: string;
    };
  };
  inventories: [
    {
      inventoryNumber: string;
    }
  ];
  agreement: {
    agreementNumber: string;
  };
}

export interface ApiClientCallParams {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  payload?: any;
  baseUrl?: string;
}